<template>
  <Dialog
    :title="internal_comment !== null ? 'Internal Order Comment' : 'Add Internal Order Comment'"
    :showCloseButton="true"
    :showFooter="true"
    :showSaveButton="false"
    :showCancelButton="false"
    :custom-footer="true"
    :sm="true"
    @closeEvent="cancelEvent"
    class="internal-note-modal"
  >
    <template #modal-content>
      <form id="form-add-product" @submit.prevent="onSubmit">
        <input
          required
          class="w-full px-2 py-4 my-6 border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          type="text"
          placeholder="Add internal note"
          v-model="internalNote"
        />
      </form>
    </template>

    <template #footer>
      <div class="flex justify-end gap-x-3">
        <button
          type="button"
          @click="cancelEvent"
          class="px-4 py-2 bg-surface text-[var(--accent)] rounded-md hover:bg-[var(--accent)] hover:text-white border border-[var(--accent)] transition-colors"
        >
          Close
        </button>
        <button
          @click="deleteInternalNote"
          v-if="internal_comment !== null"
          class="px-4 py-2 bg-surface text-red-700 rounded-md hover:bg-red-700 hover:text-white border border-red-700 transition-colors"
        >
          Delete comment
        </button>
        <button
          type="submit"
          form="form-add-product"
          class="px-4 py-2 bg-[var(--accent)] text-white rounded-md"
          :disabled="isSubmitting"
        >
          Save comment
        </button>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import Dialog from '@/components/Shared/Dialog.vue';
import { showToast } from '@/composables/swal';
import { http } from '@/interceptors/axiosRequestInterceptor';
import { ref } from 'vue';

interface InternalComment {
  comment: string;
  /** Absent when the box opens with a draft (AB#21422) rather than a saved note. */
  user_id?: number;
  user_name?: string;
  created_at?: string;
}

interface Props {
  order_item_id: number;
  internal_comment: InternalComment | null;
}

const props = withDefaults(defineProps<Props>(), {
  order_item_id: null,
  internal_comment: null,
});

const emit = defineEmits(['onClose', 'onCancel']);

const isSubmitting = ref<boolean>(false);
const internalNote = ref<string>(props.internal_comment?.comment);
const cancelEvent = () => {
  emit('onCancel');
};
const closeEvent = () => {
  emit('onClose');
};

const onSubmit = async () => {
  const payload = {
    order_item_id: props.order_item_id,
    internal_comment: internalNote.value,
  };
  try {
    isSubmitting.value = true;
    const response = await http.post(
      `${import.meta.env.VITE_APP_BASE_URL}/add-internal-comment`,
      payload
    );
    if (response.status === 200) {
      showToast(response.data.message, 'success');
    }
    closeEvent();
  } catch (e) {
    showToast(e.response?.data?.errors?.message, 'error');
    isSubmitting.value = false;
  }
};

const deleteInternalNote = async () => {
  try {
    isSubmitting.value = true;
    const response = await http.delete(
      `${import.meta.env.VITE_APP_BASE_URL}/delete-internal-comment?order_item_id=${props.order_item_id}`
    );
    if (response.status === 200) {
      showToast(response.data.message, 'success');
    }
    closeEvent();
  } catch (e) {
    showToast(e.response.data.errors.message, 'error');
    isSubmitting.value = false;
  }
};
</script>

<style lang="scss" scoped>
label {
  &:has(input:checked) {
    @apply bg-primary text-white;

    .checkIcon {
      display: block;
    }
  }
}
</style>
